import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Button } from '@draftedge/ui';
import { track } from '@/lib/analytics';
import { useAuthStore } from '@/lib/auth';
import { initialsOf } from '@/lib/initials';
import { BrandLogo } from '@/components/BrandLogo';

/**
 * Global, auth-aware top bar. Single source of truth for "are you signed in"
 * affordances so no page disagrees:
 *   - signed in  → Dashboard link + avatar (→ account) + Logout
 *   - signed out → Sign in + Get started
 *
 * The TOOLS nav (rankings, waiver wire, exposure, depth charts, injuries,
 * news) is public and shown to BOTH states — desktop inline, mobile as a
 * scrollable strip under the bar. These pages are free: they're the
 * organic front door to the draft tool.
 *
 * Mounted once in App.tsx INSIDE the BootstrapGate, so `isBootstrapping` is
 * already resolved here — the header never flashes the wrong state. (We still
 * guard defensively.)
 *
 * Hidden on the focused draft surfaces (/drafts/new, /drafts/:id) which run
 * their own full-screen chrome.
 */

const TOOL_LINKS: Array<{ to: string; label: string }> = [
  { to: '/rankings', label: 'Rankings' },
  { to: '/rookies', label: 'Rookies' },
  { to: '/trending', label: 'Waiver Wire' },
  { to: '/exposure', label: 'Exposure' },
  { to: '/depth-charts', label: 'Depth Charts' },
  { to: '/injuries', label: 'Injuries' },
  { to: '/news', label: 'News' },
];

export function SiteHeader() {
  const location = useLocation();
  const navigate = useNavigate();
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const isBootstrapping = useAuthStore((s) => s.isBootstrapping);
  const user = useAuthStore((s) => s.user);
  const logout = useAuthStore((s) => s.logout);

  // Keep the draft room / new-draft wizard distraction-free.
  if (/^\/drafts\/.+/.test(location.pathname)) {
    return null;
  }

  const handleLogout = async () => {
    await logout(); // optimistic — clears auth state synchronously first
    navigate('/', { replace: true });
  };

  const toolLink = (to: string, label: string, extra = '') => {
    const active =
      location.pathname === to || location.pathname.startsWith(`${to}/`);
    return (
      <Link
        key={to}
        to={to}
        aria-current={active ? 'page' : undefined}
        className={`whitespace-nowrap text-sm transition-colors ${
          active ? 'font-semibold text-ink' : 'text-ink-muted hover:text-ink'
        } ${extra}`}
      >
        {label}
      </Link>
    );
  };

  return (
    <header className="sticky top-0 z-40 border-b border-slate-700/50 bg-surface/95 backdrop-blur supports-[backdrop-filter]:bg-surface/80">
      <div className="mx-auto flex max-w-6xl items-center justify-between gap-4 px-4 py-3 sm:px-6">
        <div className="flex min-w-0 items-center gap-6">
          <Link to="/" aria-label="DraftEdge home" className="flex flex-none items-center">
            <BrandLogo className="h-6" />
          </Link>
          {/* Desktop tools nav — public, both auth states. */}
          <nav aria-label="NFL tools" className="hidden items-center gap-4 lg:flex">
            {TOOL_LINKS.map((l) => toolLink(l.to, l.label))}
          </nav>
        </div>

        {/* Right cluster — never rendered until bootstrap settles. */}
        {isBootstrapping ? (
          <div className="h-9 w-24" aria-hidden="true" />
        ) : isAuthenticated ? (
          <nav className="flex flex-none items-center gap-2 sm:gap-3">
            <Link to="/dashboard">
              <Button variant="ghost" size="sm">
                Dashboard
              </Button>
            </Link>
            <Link
              to="/account"
              title="Account settings"
              className="flex h-9 w-9 items-center justify-center rounded-pill bg-brand-primary text-xs font-semibold uppercase text-white transition hover:opacity-90"
            >
              {initialsOf(user?.first_name, user?.last_name, user?.email)}
            </Link>
            <Button variant="secondary" size="sm" onClick={handleLogout}>
              Logout
            </Button>
          </nav>
        ) : (
          <nav className="flex flex-none items-center gap-2 sm:gap-3">
            <Link to="/login">
              <Button variant="ghost" size="sm">
                Sign in
              </Button>
            </Link>
            <Link
              to="/register"
              onClick={() => track('cta_click', { cta_location: 'header' })}
            >
              <Button variant="primary" size="sm">
                Get started
              </Button>
            </Link>
          </nav>
        )}
      </div>

      {/* Mobile / tablet tools strip — horizontally scrollable, no drawer. */}
      <nav
        aria-label="NFL tools"
        className="flex gap-4 overflow-x-auto px-4 pb-2 sm:px-6 lg:hidden"
      >
        {TOOL_LINKS.map((l) => toolLink(l.to, l.label, 'py-0.5'))}
      </nav>
    </header>
  );
}
